import { useEffect, useState } from "react";
import type { ValidateForm } from "ra-core";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import { useSetPassword } from "ra-supabase-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Layout } from "@/components/supabase/layout";
import { getHashQueryParams } from "@/components/supabase/hashQuery";

interface FormData {
  password: string;
  confirmPassword: string;
}

/**
 * O Supabase devolve erros de auth em inglês e em linguagem de API. Quem está
 * aqui só quer saber se dá pra tentar de novo ou se precisa de outro link.
 */
const mensagemDoErro = (error: any): string => {
  const bruto =
    typeof error === "string" ? error : (error?.message ?? "");

  if (/expired|invalid.*(jwt|token)|token.*(invalid|not found)|session/i.test(bruto)) {
    return "Link expirado ou já utilizado. Solicite um novo em “Esqueceu a senha?”.";
  }
  if (/should be different|same.*password/i.test(bruto)) {
    return "A nova senha precisa ser diferente da anterior.";
  }
  if (/at least|too short|length/i.test(bruto)) {
    return "A senha deve ter pelo menos 6 caracteres.";
  }
  if (/weak|pwned|compromised/i.test(bruto)) {
    return "Senha fraca. Escolha outra senha.";
  }
  return bruto || "Não foi possível salvar a senha.";
};

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  // Os tokens vêm no hash da rota (`/#/set-password?access_token=…`), colocados
  // lá pelo auth-callback.html a partir do fragmento que o Supabase devolve.
  const params = getHashQueryParams();
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  const semTokens = !access_token || !refresh_token;

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [, { mutateAsync: setPassword }] = useSetPassword();

  // Sem token não há o que fazer nesta tela — e ficar nela sem explicação é o
  // que fazia o reset parecer quebrado. Manda pedir outro link.
  useEffect(() => {
    if (semTokens) {
      redirect("/forgot-password?motivo=tokens_ausentes");
    }
  }, [semTokens, redirect]);

  const validate = (values: FormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    return {};
  };

  if (semTokens) {
    return (
      <Layout>
        <p>{translate("ra-supabase.auth.missing_tokens")}</p>
      </Layout>
    );
  }

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      await setPassword({
        access_token,
        refresh_token,
        password: values.password,
      });
    } catch (error: any) {
      notify(mensagemDoErro(error), { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-[-0.02em] text-marinho">
          {translate("ra-supabase.set_password.new_password", {
            _: "Definição de senha",
          })}
        </h1>
      </div>
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
        validate={validate as ValidateForm}
      >
        <TextInput
          label={translate("ra-supabase.auth.password_label", {
            _: "Senha",
          })}
          autoComplete="new-password"
          source="password"
          type="password"
          validate={required()}
        />
        <TextInput
          label={translate("ra-supabase.auth.confirm_password", {
            _: "Confirmar senha",
          })}
          source="confirmPassword"
          type="password"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("ra.action.save")}
        </Button>
      </Form>
    </Layout>
  );
};

SetPasswordPage.path = "set-password";
